import { Toaster } from "@/components/ui/sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Route, Routes, Navigate, useParams, useLocation } from 'react-router-dom';
import { useEffect, Suspense } from 'react';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { BusinessProvider } from '@/lib/BusinessContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import InstallPrompt from '@/components/shared/InstallPrompt';

const PageSpinner = () => (
  <div className="fixed inset-0 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
  </div>
);

function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname, search]);
  return null;
}

// Slug map: plural URL slug → category value used by the API
const SLUG_TO_CATEGORY = {
  restaurants: "restaurant", hotels: "hotel", guesthouses: "guesthouse",
  lodges: "lodge", resorts: "resort", inns: "inn", cafes: "cafe",
  bakeries: "bakery", bars: "bar", eateries: "eatery",
  hospitals: "hospital", clinics: "clinic", pharmacies: "pharmacy",
  salons: "salon", barbershops: "barbershop", gyms: "gym",
  markets: "market", supermarkets: "supermarket", shops: "shop",
  banks: "bank", schools: "school",
  "car-rentals": "car_rental", "tour-companies": "tour_company",
  taxis: "taxi", "airport-transfers": "airport_transfer",
  beaches: "beach", attractions: "attraction", parks: "park",
  mechanics: "mechanic", "apartment-rentals": "apartment_rental",
  tailors: "tailoring", "internet-cafes": "internet_cafe",
};

const VALID_AREAS = new Set([
  "banjul", "kanifing", "serrekunda", "bakau", "fajara", "kololi", "kotu",
  "senegambia", "bijilo", "brufut", "brikama", "sukuta", "gunjur", "sanyang",
  "kartong", "tanji", "cape_point", "abuko", "yundum", "tendaba",
  "bansang", "janjanbureh", "basse", "farafenni", "kerewan",
  "kaur", "kuntaur", "soma", "mansa_konko",
]);

// Handles pretty URLs like /restaurants-in-serrekunda
function SEOLandingRoute() {
  const { slug } = useParams();
  const inIdx = slug.lastIndexOf("-in-");
  if (inIdx === -1) return <PageNotFound />;
  const categorySlug = slug.slice(0, inIdx);
  const areaSlug = slug.slice(inIdx + 4);
  const category = SLUG_TO_CATEGORY[categorySlug];
  const area = VALID_AREAS.has(areaSlug) ? areaSlug.toUpperCase() : null;
  if (!category || !area) return <PageNotFound />;
  return <Navigate to={`/CategoryAreaLanding?category=${category}&area=${area}`} replace />;
}

// Pages that require any authenticated user (route-level guard, eliminates flash on redirect)
const AUTH_PAGES = new Set([
  'Cart', 'Checkout', 'OrderConfirmation', 'BookingForm',
  'MyBookings', 'MyOrders', 'MyTickets', 'MyClaims', 'MyCalendar', 'MyReviews', 'MyEnquiries', 'MyDeals', 'Notifications',
  'SavedBusinesses', 'SavedDeals', 'FollowingBusinesses',
  'ProfileSettings', 'RealTimeNotificationsSetup', 'UserDashboard', 'ClaimBusiness',
  'OwnerDashboard', 'OwnerAnalytics', 'OwnerSettings', 'OwnerVerification',
  'ManageBookings', 'ManageDeals', 'ManageEvents', 'ManageExperiences', 'ManageGallery',
  'ManageMenu', 'ManageOrders', 'ManageEnquiries', 'ManageReviews',
  'AvailabilityManagement', 'BusinessBookingSetup', 'BookingAnalytics', 'TeamMembers',
  'AddBusiness', 'EditBusiness', 'BusinessOnboarding', 'MyBusinesses',
]);

// Pages that require ADMIN role
const ADMIN_PAGES = new Set([
  'AdminDashboard', 'AdminAnalytics', 'AdminUsers', 'AdminBusinesses', 'AdminImport',
  'AdminBookings', 'AdminDeals', 'AdminEvents', 'AdminExperiences', 'AdminReviews',
  'AdminReports', 'AdminBlog', 'AdminCategories', 'AdminVerifications', 'AdminFeedback',
  'AdminAuditLog', 'AdminCampaigns',
]);

function ProtectedRoute({ children }) {
  const { user, isLoadingAuth } = useAuth();
  const location = useLocation();
  if (isLoadingAuth) return <PageSpinner />;
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/Login?returnUrl=${returnUrl}`} replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { user, isLoadingAuth } = useAuth();
  const location = useLocation();
  if (isLoadingAuth) return <PageSpinner />;
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/Login?returnUrl=${returnUrl}`} replace />;
  }
  if (user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') return <Navigate to="/" replace />;
  return children;
}

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError } = useAuth();

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    }
    // 'auth_required' just means the stored token was rejected — allow the app
    // to load so the user can navigate normally and log in via the Login page.
  }

  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/" element={
          <LayoutWrapper currentPageName={mainPageKey}>
            <MainPage />
          </LayoutWrapper>
        } />
        {Object.entries(Pages).map(([path, Page]) => {
          const inner = (
            <LayoutWrapper currentPageName={path}>
              <Page />
            </LayoutWrapper>
          );
          const element = ADMIN_PAGES.has(path)
            ? <AdminRoute>{inner}</AdminRoute>
            : AUTH_PAGES.has(path)
              ? <ProtectedRoute>{inner}</ProtectedRoute>
              : inner;
          return <Route key={path} path={`/${path}`} element={element} />;
        })}
        {/* SEO pretty URLs: /restaurants-in-serrekunda → CategoryAreaLanding */}
        <Route path="/:slug" element={
          <LayoutWrapper currentPageName="CategoryAreaLanding"><SEOLandingRoute /></LayoutWrapper>
        } />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <BusinessProvider>
          <Router>
            <ScrollToTop />
            <AuthenticatedApp />
          </Router>
          <Toaster />
          <InstallPrompt />
        </BusinessProvider>
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App